import { Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Grid } from '@mui/material'
import { MobileDatePicker } from '@mui/x-date-pickers'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { FormContext } from '../Contexts'
import { PersonalDataValidationSchema } from './ValidationSchemas'
import { SelectRegistered, TextFieldRegistered, TextMaskFieldRegistered } from '../Components'

import { FormHandleSubmit, GetSchemaValidation } from '../types'
import { bloodTypes, cities, documentTypes, maritalStatusList, nationalities, states } from '../../../data'

export const PersonalDataStep = ({
  id,
  data,
  setActiveForm,
}: {
  id: string
  data?: GetSchemaValidation<typeof PersonalDataValidationSchema>
  setActiveForm: (
    handleSubmit: () => FormHandleSubmit<GetSchemaValidation<typeof PersonalDataValidationSchema>>,
  ) => void
}) => {
  const { control, handleSubmit, register, setValue, watch, formState, getValues } = useForm({
    resolver: yupResolver(PersonalDataValidationSchema),
    defaultValues: data,
  })
  const posees = watch('posees') || []

  useEffect(() => {
    setActiveForm(() => ({ formId: id, getData: getValues, submit: handleSubmit }))
  }, [getValues, handleSubmit, id, setActiveForm])

  return (
    <FormContext.Provider value={{ control, handleSubmit, register, setValue, watch, formState }}>
      <Grid container spacing={2} rowSpacing={2}>
        <Grid item xs={12} sm={6} md={6}>
          <TextFieldRegistered required label="Nombre" name="name" />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextFieldRegistered required label="Apellidos" name="surname" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SelectRegistered
            name="nationality"
            label="Nacionalidad"
            required
            options={nationalities.map((nationality) => ({ label: nationality }))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SelectRegistered
            name="birthcity"
            label="Ciudad de Nacimiento"
            required
            options={cities.map((city) => ({ label: city }))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MobileDatePicker
            label="Fecha de Nacimiento"
            format="dd/MM/yyyy"
            disableFuture={true}
            closeOnSelect={true}
            slotProps={{
              actionBar: {
                actions: ['clear'],
              },
              textField: {
                variant: 'standard',
                fullWidth: true,
                required: true,
              },
            }}
            {...register('birthdate')}
            value={watch('birthdate')}
            onChange={(value) => setValue?.('birthdate', value || new Date())}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <SelectRegistered
            name="documentType"
            label="Tipo de Documento"
            required
            options={documentTypes.map((documentType) => ({ label: documentType }))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextFieldRegistered required label="Número del documento" name="document" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SelectRegistered
            name="maritalStatus"
            label="Estado civil"
            required
            options={maritalStatusList.map((maritalStatus) => ({ label: maritalStatus }))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <SelectRegistered name="gender" label="Sexo" options={[{ label: 'Feminino' }, { label: 'Masculino' }]} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <SelectRegistered
            name="bloodType"
            label="Tipo sangüineo"
            options={bloodTypes.map((bloodType) => ({ label: bloodType }))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextFieldRegistered label="PADRE - Nombres y Apellidos" name="fatherName" />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextFieldRegistered label="MADRE - Nombres y Apellidos" name="motherName" />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextFieldRegistered
            required={['Casado', 'Concubinato'].includes(watch('maritalStatus'))}
            label="CONYUGUE - Nombres y Apellidos"
            name="partnerName"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextFieldRegistered
            required={['Casado', 'Concubinato'].includes(watch('maritalStatus'))}
            name="children"
            label="Número de hijos"
            type="number"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextFieldRegistered required name="address.street" label="Dirección - Avenida o Calle" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextFieldRegistered required name="address.number" label="Número del inmueble" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextFieldRegistered required name="address.neighborhood" label="Barrio" />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <SelectRegistered
            name="address.city"
            required
            label="Ciudad donde reside"
            options={cities.map((city) => ({ label: city }))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <SelectRegistered
            name="address.state"
            required
            label="Departamento"
            options={states.map((state) => ({ label: state }))}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextFieldRegistered required name="email" label="E-mail" type="email" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextMaskFieldRegistered
            required
            label="Número del celular"
            name="mobile"
            mask={(value) => (value?.toString()?.length === 9 ? '(\\0 000) 000000' : '(\\0 00) 000000[0]')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextMaskFieldRegistered
            label="Número de línea fija"
            name="phone"
            mask={(value) => (value?.toString()?.length === 9 ? '(\\0 000) 000000' : '(\\0 00) 000000[0]')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <SelectRegistered
            name="experience"
            label="¿Tienes experiencia formal de trabajo?"
            options={[
              { label: 'Sí - con IPS' },
              { label: 'No - no tenía IPS' },
              { label: 'Sí con IVA' },
              { label: 'Primer empleo' },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextFieldRegistered label="Instagram" name="instagram" />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextFieldRegistered label="Facebook" name="facebook" />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextFieldRegistered label="LinkedIn" name="linkedin" />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextFieldRegistered label="Pinterest" name="pinterest" />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextFieldRegistered label="X Social" name="xsocial" />
        </Grid>
        <Grid item xs={12} textAlign="left">
          <FormControl component="fieldset">
            <FormLabel component="legend">Señale abajo los bienes que posee</FormLabel>
            <FormGroup row>
              <FormControlLabel
                value="Casa propia"
                control={<Checkbox />}
                label="Casa propia"
                checked={posees?.includes?.('Casa propia')}
                {...register('posees')}
              />
              <FormControlLabel
                value="Auto"
                control={<Checkbox />}
                label="Auto"
                checked={posees?.includes?.('Auto')}
                {...register('posees')}
              />
              <FormControlLabel
                value="Motocicleta"
                control={<Checkbox />}
                label="Motocicleta"
                checked={posees?.includes?.('Motocicleta')}
                {...register('posees')}
              />
            </FormGroup>
          </FormControl>
        </Grid>
      </Grid>
    </FormContext.Provider>
  )
}
